import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { getCurrentUser } from "@/lib/session";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui";

const INTERNAL_ROLES = new Set([
  "mfx_admin", "mfx_pm", "mfx_compliance", "mfx_settlements",
]);

/**
 * Gate a client-facing (sandbox) route by its path. Internal staff always
 * see it (so they can preview before flipping it on); clients see it only
 * while it's switched client-facing. Blocks direct-URL access too.
 */
export function ClientGate({ path, children }: { path: string; children: ReactNode }) {
  const user = getCurrentUser();
  const isInternal = INTERNAL_ROLES.has(user?.role) || user?.isSuperAdmin === true;

  const { data, isLoading } = useQuery({
    queryKey: ["client-facing-pages", user?.role],
    queryFn: () => api.getMyClientFacingPages(),
    staleTime: 5 * 60_000,
    enabled: !isInternal,
  });

  if (isInternal) return <>{children}</>;
  if (isLoading) return null;

  const allowed = new Set(data?.paths ?? []);
  if (allowed.has(path)) return <>{children}</>;

  return (
    <div>
      <PageHeader title="Not available" subtitle="" />
      <Card padding="lg">
        <p className="text-sm text-dark-500">
          This page isn't available on your account.
        </p>
      </Card>
    </div>
  );
}
